// Activity.tsx (v4) — the Activity feed leads with two causal heroes wired to
// LIVE data from the #1951 endpoints:
//   • System Pulse   ← /api/pwa/heroes (systemPulse, 24h execution shape) enriched
//                       with the /api/pwa/daily-digest expanded daily digest.
//   • Attention Queue ← /api/pwa/heroes (attentionQueue) + operator_actions table.
//
// STEP-OV-ACTIVITY-REWORK:
//   - VLR items are filtered out of the attention queue (not operator-actionable).
//   - Proposals flow via attentionQueue.proposals (heroes API) and are rendered
//     by AttentionQueueHero's ProposalActionItems section (already wired).
//   - Daily digest appears above the queue from /api/pwa/daily-digest (STEP-PWA-ACTIVITY-DAILY-DIGEST).

import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client.js";
import type {
  AttentionQueueData,
  SystemPulseData,
} from "../../api/types.js";
import {
  SystemPulseHero,
  AttentionQueueHero,
} from "../../components/Heroes.js";
import { OpsReportView } from "../../screens/OperationalViews.js";
import { useTimeShift } from "../TimeShift.js";
import { ConnectivityBanner } from "../../components/ConnectivityBanner.js";
import { ActivityDailyDigest } from "./ActivityDailyDigest.js";

/** Filter the attention queue to operator-actionable items only.
 *
 * VLR rows are system-generated noise — they don't require operator action
 * and clutter the queue. Pending proposals surface via data.proposals (wired
 * through the heroes API to ProposalActionItems in AttentionQueueHero).
 */
export function filterQueueForOperator(queue: AttentionQueueData | undefined): AttentionQueueData | undefined {
  if (!queue) return undefined;
  const filtered = queue.items.filter((item) => item.kind !== "vlr");
  // Recount metric value to match filtered list so the hero headline stays accurate.
  const filteredCount = filtered.length;
  return {
    ...queue,
    items: filtered,
    metric: {
      ...queue.metric,
      value: String(filteredCount),
    },
  };
}

export function ActivityScreen() {
  const { asOf, isLive } = useTimeShift();
  const [pulse, setPulse] = useState<SystemPulseData | undefined>(undefined);
  const [queue, setQueue] = useState<AttentionQueueData | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const heroes = await api.heroes(asOf);
      // DEC-166: a response missing BOTH heroes is a malformed/failed load,
      // not a quiet system — never let it render as calm "System idle".
      // Genuine idle arrives as a present systemPulse with zero activity.
      if (!heroes.systemPulse && !heroes.attentionQueue) {
        throw new Error("malformed heroes payload");
      }
      setPulse(heroes.systemPulse);
      setQueue(heroes.attentionQueue);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoaded(true);
    }
  }, [asOf]);

  useEffect(() => {
    void load();
    // A scrubbed (historical) view is frozen — no point polling it.
    if (!isLive) return;
    const interval = setInterval(() => void load(), 30_000);
    return () => clearInterval(interval);
  }, [load, isLive]);

  if (!loaded && !loadError) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "var(--text-dim)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        Loading activity…
      </div>
    );
  }

  if (loadError && !pulse && !queue) {
    return <ConnectivityBanner error={loadError} variant="block" />;
  }

  // Attention queue filtered to operator-actionable items (VLRs removed).
  // This is broader than Home's "Blocked Steps" tile which shows only
  // operator_queue.waiting from home-digest. The hero eyebrow/headline
  // from the server distinguishes the two surfaces.
  const filteredAttentionQueue = filterQueueForOperator(queue);

  return (
    <>
      {loadError != null && <ConnectivityBanner error={loadError} variant="inline" />}

      <SystemPulseHero data={pulse} />

      {/* Daily digest — STEP-PWA-ACTIVITY-DAILY-DIGEST: expanded 24h summary with
          delta framing, 8h refresh cadence, explicit empty sections, STALE/UNAVAILABLE
          states. Replaces the single-sentence snapshot.narrative. */}
      <div style={{ borderBottom: "1px solid var(--border-soft)" }}>
        <ActivityDailyDigest />
      </div>

      <div style={{ height: 24 }} />

      {!isLive && (
        // The attention queue has no temporal archive — it degrades to LIVE
        // state when scrubbed, flagged so the operator isn't misled.
        <div
          style={{
            padding: "0 20px 8px",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-dim)",
          }}
        >
          ● LIVE — attention queue has no history; showing current state
        </div>
      )}

      <AttentionQueueHero
        data={filteredAttentionQueue}
        onNavigate={(intent) => window.__goTab?.(intent.tab)}
        onReload={load}
      />

      {/* Ops Report — moved from Dispatch tab (STEP-PWA-OPS-REPORT-INTO-ACTIVITY).
          Shows dispatch job outcomes (total/complete/failed/running, cost) and a
          per-type event breakdown. These are distinct signals from the SystemPulseHero
          above: pulse shows hourly event volume; this shows job-level outcomes and
          the type dimension of the same event stream. */}
      <div
        style={{
          padding: "8px 20px 6px",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-dim)",
          borderBottom: "1px solid var(--border-soft)",
          marginTop: 8,
        }}
      >
        Dispatch jobs &amp; events (24h) — job outcomes, not the hourly pulse above
      </div>
      <OpsReportView />
    </>
  );
}
